
import React, { useState, useEffect } from 'react';
import {
  Search,
  Phone,
  Mail,
  Folder,
  Send,
  Plus,
  Trash2,
  Bell
} from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import StudentDetailModal from '../Modals/StudentDetailModal';
import AddStudentModal from '../Modals/AddStudentModal';
import SmsReminderModal from '../Modals/SmsReminderModal';

interface DbStudent {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  nationality: string;
  created_at: string;
  is_registered?: boolean;
  branch_id?: string;
  branches?: {
    name: string;
  };
  display_branch_name?: string;
  source?: 'operations' | 'regular';
  leads?: {
    status: string;
    priority: string;
    last_contact?: string;
    contact_role?: string;
    first_name?: string;
    last_name?: string;
    student_info?: {
      first_name?: string;
      last_name?: string;
    };
    branch_id?: string;
  }[];
  documents?: {
    id: string;
    status: string;
  }[];
}

const StudentList: React.FC = () => {
  const { user } = useAuth();
  const [students, setStudents] = useState<DbStudent[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<DbStudent | null>(null);
  const [debugInfo, setDebugInfo] = useState<{ opCount: number; regCount: number; err?: string }>({ opCount: 0, regCount: 0 });
  const [smsModalOpen, setSmsModalOpen] = useState(false);

  useEffect(() => {
    fetchStudents();
  }, [user?.id]);

  const fetchStudents = async () => {
    try {
      setLoading(true);

      const branchesQuery = supabase.from('branches').select('id, name');
      const { data: branchesData } = user?.branchId && user?.role !== 'admin'
        ? await branchesQuery.eq('id', user.branchId)
        : await branchesQuery;
      const branchMap = new Map((branchesData || []).map(b => [b.id, b.name]));
      let combinedStudents: DbStudent[] = [];
      let regularStudents: DbStudent[] = [];

      // Fetch from operation_students (safe fetch)
      // Fetch operations students (Skip for Admin)
      if (user?.role !== 'admin') {
        try {
          // RLS handles the filtering (Operations see branch or created_by)
          let operationStudentsQuery = supabase
            .from('operation_students')
            .select('*')
            .order('created_at', { ascending: false });

          if (user?.branchId) operationStudentsQuery = operationStudentsQuery.eq('branch_id', user.branchId);
          const { data: opData, error: opError } = await operationStudentsQuery;

          if (opError) {
            console.error('Operation Students Fetch Error:', opError);
            setDebugInfo(prev => ({ ...prev, err: 'OpFetch: ' + opError.message }));
          }

          if (opData) {
            const mappedOpStudents = opData.map((op: any) => ({
              id: op.id,
              first_name: op.first_name,
              last_name: op.last_name,
              email: op.email,
              phone: op.phone,
              nationality: 'Türkiye',
              created_at: op.created_at,
              branch_id: op.branch_id,
              display_branch_name: op.branch_id ? branchMap.get(op.branch_id) : '-',
              source: 'operations' as const,
              leads: [{
                status: op.status || 'active',
                priority: 'high',
                last_contact: op.created_at
              }]
            }));
            combinedStudents = [...mappedOpStudents];
          }
        } catch (opErr) {
          console.warn('Could not fetch operation_students', opErr);
        }
      }

      if (user?.unitType === 'education_consultant' || user?.unitType === 'operations') {
        let contractQuery = supabase
          .from('contracts')
          .select('student_id')
          .eq('transferred_to_operations', true);

        if (user?.branchId) {
          contractQuery = contractQuery.eq('branch_id', user.branchId);
        }

        const { data: contractData, error: contractError } = await contractQuery;

        if (contractError) throw contractError;

        const studentIds = contractData?.map(c => c.student_id) || [];

        if (studentIds.length > 0) {
          const { data, error } = await supabase
            .from('students')
            .select(`
              id,
              first_name,
              last_name,
              email,
              phone,
              nationality,
              created_at,
              branch_id
            `)
            .in('id', studentIds)
            .order('created_at', { ascending: false });

          if (error) throw error;
          regularStudents = (data || []).map((s: any) => ({
            ...s,
            source: 'regular' as const,
            display_branch_name: (s.branch_id ? branchMap.get(s.branch_id) : undefined) || '-'
          }));
          combinedStudents = [...combinedStudents, ...regularStudents];
        }
      } else {
        const { data, error } = await supabase
          .from('students')
          .select(`
            id,
            first_name,
            last_name,
            email,
            phone,
            nationality,
            created_at,
            branch_id,
            branches (
              name
            ),
            leads (
              status,
              priority,
              contact_role,
              first_name,
              last_name,
              student_info,
              branch_id,
              is_registered,
              tracking_status
            )
          `)
          .order('created_at', { ascending: false });

        if (error) throw error;
        regularStudents = (data || []).map((s: any) => ({
          ...s,
          source: 'regular',
          display_branch_name: (s.branches?.name || (s.leads?.[0]?.branch_id ? branchMap.get(s.leads[0].branch_id) : undefined)) || '-'
        }));

        // Admin filtering: Show students that are marked as enrolled/registered in ANY way
        if (user?.role === 'admin') {
          regularStudents = regularStudents.filter((s: any) =>
            s.leads && s.leads.some((l: any) =>
              l.status === 'enrolled' ||
              l.is_registered === true ||
              l.tracking_status === 'enrolled'
            )
          );
        }

        combinedStudents = [...combinedStudents, ...regularStudents];
      }

      // Remove duplicates if any (though ids should be unique across tables effectively for this view)
      setStudents(combinedStudents);

      console.log('Total Operation Students:', combinedStudents.length - (regularStudents?.length || 0));
      console.log('Total Regular Students:', regularStudents?.length || 0);
      setDebugInfo({
        opCount: combinedStudents.length - (regularStudents?.length || 0),
        regCount: regularStudents?.length || 0
      });

    } catch (error: any) {
      console.error('Öğrenciler yüklenirken hata:', error);
      setDebugInfo(prev => ({ ...prev, err: error.message }));
    } finally {
      setLoading(false);
    }
  };

  const handleAddStudent = async (studentData: any) => {
    try {
      const isFullMode = studentData.type === 'student_full';

      if (isFullMode) {
        // --- OPERATIONS MODE ---
        // Request received to use a dedicated 'operation_students' table.
        // We will insert into 'operation_students' table.
        // We serialize the details into 'notes'.

        const noteContent = `
  === DETAYLAR ===
  Bölümler: ${studentData.interested_programs?.join(', ')}

--- SINAVLAR-- -
  ${studentData.exams?.map((e: any) => `${e.date || ''} ${e.name}: ${e.score} (${e.note})`).join('\n')}

--- DİL BELGELERİ-- -
  ${studentData.language_docs?.map((d: any) => `${d.name} (${d.level}): ${d.note}`).join('\n')}

--- BAŞVURULAR-- -
  ${studentData.applications?.map((a: any) => `${a.schoolName} - ${a.department} (${a.status})`).join('\n')}

--- UNIVERSITALY-- -
  ${studentData.universitaly ? JSON.stringify(studentData.universitaly) : 'Yok'}
`.trim();

        // Safe Insertion into operation_students
        const insertPayload: any = {
          first_name: studentData.first_name,
          last_name: studentData.last_name,
          email: studentData.email,
          phone: studentData.phone,
          status: 'active',
          education_level: studentData.education_level,
          academic_year: studentData.academic_year,
          notes: noteContent
        };

        // Add created_by and branch_id if current user exists
        if (user?.id) {
          insertPayload.created_by = user.id;
          if (user.branchId) {
            insertPayload.branch_id = user.branchId;
          }
        }

        const { data, error } = await supabase
          .from('operation_students')
          .insert(insertPayload)
          .select()
          .single();

        if (error) {
          console.error('Operation Table Insert Error:', error);
          if (error.code === '42P01') {
            // Table missing
            alert("Hata: 'operation_students' tablosu veritabanında bulunamadı.");
          } else if (error.code === '42703' || error.code === 'PGRST204') {
            // Try fallback without created_by if schema is strict
            delete insertPayload.created_by;
            const { data: retryData, error: retryError } = await supabase
              .from('operation_students')
              .insert(insertPayload)
              .select()
              .single();

            if (!retryError) {
              alert(`${studentData.first_name} ${studentData.last_name} operasyon listesine eklendi.`);
              fetchStudents();
              return { data: retryData, error: null };
            }
            console.error('Retry failed:', retryError);
            alert(`Hata: Tablo şeması eksik(${error.message}).`);
          } else {
            alert(`Hata: ${error.message} `);
          }
          return { data: null, error };
        }

        if (data) {
          try {
            await supabase.from('activity_logs').insert({
              action_type: 'new_student',
              entity_type: 'student',
              entity_id: data.id,
              title: `${studentData.first_name} ${studentData.last_name}`,
              description: 'Operasyon listesine yeni öğrenci eklendi',
              created_by: user?.id
            });
          } catch (logErr) {
            console.warn('Log insert failed:', logErr);
          }
        }

        alert(`${studentData.first_name} ${studentData.last_name} operasyon listesine eklendi.`);
        fetchStudents();
        return { data, error: null };

      } else {
        // --- CLASSIC LEAD / SALES MODE ---

        // Removed 'created_by' from base insert to fix schema error reported by user
        const baseInsertData = {
          first_name: studentData.first_name,
          last_name: studentData.last_name,
          email: studentData.email,
          phone: studentData.phone,
          nationality: studentData.nationality || 'Türkiye'
        };

        // 1. Create Student
        const { data: student, error: studentError } = await supabase
          .from('students')
          .insert(baseInsertData)
          .select()
          .single();

        if (studentError) {
          console.error('Student creation error:', studentError);
          if (studentError.code === 'PGRST204' || studentError.code === '42703') {
            alert("Hata: Veritabanı şeması uyuşmazlığı. 'students' tablosunda beklenen kolonlar yok.");
          }
          throw studentError;
        }

        // 2. Create Lead
        const leadInsertData = {
          student_id: student.id,
          status: 'lead',
          source: studentData.source || 'manual',
          lead_temperature: studentData.priority || 'warm',
          ...baseInsertData
        };

        const { error: leadError } = await supabase
          .from('leads')
          .insert(leadInsertData);

        if (leadError) console.warn('Lead creation warning:', leadError);

        if (student) {
          try {
            await supabase.from('activity_logs').insert({
              action_type: 'new_student',
              entity_type: 'student',
              entity_id: student.id,
              title: `${studentData.first_name} ${studentData.last_name}`,
              description: 'Sisteme yeni öğrenci eklendi',
              created_by: user?.id
            });
          } catch (logErr) {
            console.warn('Log insert failed:', logErr);
          }
        }

        fetchStudents();
        return { data: student, error: null };
      }

    } catch (error: any) {
      console.error('Error adding student:', error);
      alert('İşlem sırasında hata oluştu: ' + (error.message || 'Bilinmeyen Hata'));
      return { data: null, error };
    }
  };

  const handleDeleteStudent = async (student: DbStudent) => {
    if (!await window.appConfirm(`${student.first_name} ${student.last_name} adlı öğrenciyi silmek istediğinize emin misiniz?`)) return;

    setLoading(true);
    try {
      // Log deletion before deleting the record (since record will be gone)
      try {
        await supabase.from('activity_logs').insert({
          action_type: 'delete_student',
          entity_type: 'student',
          entity_id: student.id,
          title: `${student.first_name} ${student.last_name}`,
          description: 'Öğrenci kaydı silindi',
          created_by: user?.id
        });
      } catch (logErr) {
        console.warn('Logging failed (activity_logs table might be missing):', logErr);
      }

      if (student.source === 'operations') {
        const { error } = await supabase
          .from('operation_students')
          .delete()
          .eq('id', student.id);
        if (error) throw error;
      } else {
        // Regular student deletion might be more complex due to constraints (leads, contracts, etc.)
        // First try to delete dependent lead if exists? Or rely on CASCADE.
        // Assuming CASCADE is set up or we just delete student.
        const { error } = await supabase
          .from('students')
          .delete()
          .eq('id', student.id);
        if (error) throw error;
      }
      alert('Öğrenci başarıyla silindi.');
      fetchStudents();
    } catch (error: any) {
      console.error('Error deleting student:', error);
      alert('Silme işleminde hata oluştu: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
      case 'contacted':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      case 'interested':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'applied':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400';
      case 'accepted':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'rejected':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      case 'enrolled':
        return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'new':
        return 'Yeni';
      case 'contacted':
        return 'İletişim Kuruldu';
      case 'interested':
        return 'İlgili';
      case 'applied':
        return 'Başvuru Yapıldı';
      case 'active':
        return 'Aktif';
      case 'accepted':
        return 'Kabul Edildi';
      case 'rejected':
        return 'Reddedildi';
      case 'enrolled':
        return 'Kayıt Oldu';
      default:
        return status;
    }
  };
  const filteredStudents = students.filter((student) => {
    const matchesSearch =
      student.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.phone?.includes(searchTerm);

    const studentLeadStatus = student.leads && student.leads.length > 0 ? student.leads[0].status : '';

    const matchesStatus = statusFilter === 'all' || studentLeadStatus === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const handleSendToOperations = async (student: DbStudent) => {
    if (!user) return;

    if (!await window.appConfirm(`${student.first_name} ${student.last_name} öğrenciyi operasyon birimine göndermek istediğinize emin misiniz ? `)) {
      return;
    }

    try {
      const { data: existingContract } = await supabase
        .from('contracts')
        .select('id, transferred_to_operations')
        .eq('student_id', student.id)
        .maybeSingle();

      if (existingContract?.transferred_to_operations) {
        alert('Bu öğrenci zaten operasyon birimine gönderilmiş.');
        return;
      }

      if (existingContract && !existingContract.transferred_to_operations) {
        const { error } = await supabase
          .from('contracts')
          .update({
            transferred_to_operations: true,
            transferred_to_operations_at: new Date().toISOString(),
            transferred_by: user.id
          })
          .eq('id', existingContract.id);

        if (error) throw error;
      } else {
        const contractNumber = `SOZ - ${new Date().getFullYear()} -${Math.random().toString(36).substr(2, 6).toUpperCase()} `;

        const { error } = await supabase
          .from('contracts')
          .insert({
            student_id: student.id,
            branch_id: user.branchId || '126e8dc2-b4af-42a4-ac78-ab5aecc9ac6a',
            contract_number: contractNumber,
            contract_date: new Date().toISOString().split('T')[0],
            total_amount: 0,
            currency: 'EUR',
            status: 'active',
            created_by: user.id,
            transferred_to_operations: true,
            transferred_to_operations_at: new Date().toISOString(),
            transferred_by: user.id
          });

        if (error) throw error;
      }

      alert('Öğrenci başarıyla operasyon birimine gönderildi!');
      await fetchStudents();
    } catch (error) {
      console.error('Operasyona gönderme hatası:', error);
      alert('Öğrenci gönderilirken bir hata oluştu.');
    }
  };

  const handleStudentAction = (action: string, student: DbStudent) => {
    switch (action) {
      case 'documents':
        setSelectedStudent(student);
        setShowDocumentsModal(true);
        break;
      case 'sendToOperations':
        handleSendToOperations(student);
        break;
      case 'call':
        if (window.makeKarelCall && student.phone) {
          const success = window.makeKarelCall(student.phone);
          if (success) {
            console.log(
              `${student.first_name} ${student.last_name} aranıyor: ${student.phone} `
            );
          } else {
            const cleanPhone = student.phone.replace(/\D/g, '');
            const message = encodeURIComponent(
              `Merhaba ${student.first_name}, Pisa Educazione'den arıyorum. Size nasıl yardımcı olabilirim?`
            );
            window.open(`https://wa.me/${cleanPhone}?text=${message}`, '_blank');
          }
        } else {
          alert('Telefon numarası bulunamadı veya Karel sistemi aktif değil.');
        }
        break;
      case 'delete':
        handleDeleteStudent(student);
        break;
      default:
        console.log(`Action: ${action} for student: ${student.id}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {user?.role === 'admin' ? 'Tüm Öğrenciler' : 'Öğrenci Evrak Takibi'}
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Toplam {filteredStudents.length} öğrenci
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSmsModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            <Bell className="w-4 h-4" />
            SMS Hatırlatıcı
          </button>
          {(user?.unitType === 'operations' || user?.unitType === 'education_consultant' || user?.role === 'admin') && (
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Yeni Öğrenci Ekle
            </button>
          )}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Öğrenci ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">Tüm Durumlar</option>
            <option value="new">Yeni</option>
            <option value="contacted">İletişim Kuruldu</option>
            <option value="interested">İlgili</option>
            <option value="applied">Başvuru Yapıldı</option>
            <option value="accepted">Kabul Edildi</option>
            <option value="rejected">Reddedildi</option>
            <option value="enrolled">Kayıt Oldu</option>
          </select>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">
                  Öğrenci
                </th>
                <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">
                  İletişim
                </th>
                <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">
                  Durum
                </th>
                <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">
                  Şube
                </th>
                <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">
                  Kayıt Tarihi
                </th>
                <th className="text-right py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">
                  İşlemler
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredStudents.map((student) => {

                return (
                  <tr
                    key={student.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <td className="py-4 px-6">
                      {(() => {
                        const regularName = (student.first_name + ' ' + (student.last_name || '')).trim();
                        let displayName = regularName;
                        let isParent = false;
                        let studentRealName = '';

                        if (!displayName && student.leads && student.leads.length > 0) {
                          const lead = student.leads[0];
                          if (lead.contact_role === 'guardian') {
                            displayName = (lead.first_name + ' ' + (lead.last_name || '')).trim();
                            isParent = true;
                            if (lead.student_info?.first_name) {
                              studentRealName = (lead.student_info.first_name + ' ' + (lead.student_info.last_name || '')).trim();
                            }
                          } else if (lead.contact_role === 'student' && !displayName) {
                            displayName = (lead.first_name + ' ' + (lead.last_name || '')).trim();
                          }
                        }

                        if (!displayName) displayName = 'İsimsiz Öğrenci';

                        return (
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                              <span className="text-white text-sm font-semibold">
                                {displayName.charAt(0)}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">
                                {displayName}
                                {isParent && <span className="text-xs text-gray-400 ml-1">(Veli)</span>}
                              </p>
                              {isParent && studentRealName && (
                                <p className="text-xs text-blue-500">
                                  Öğr: {studentRealName}
                                </p>
                              )}
                              <div className="text-sm text-gray-500 dark:text-gray-400 flex flex-col">
                                <span>
                                  {student.nationality || 'Belirtilmemiş'}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="py-4 px-6">
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                          <Mail className="w-4 h-4" />
                          <span className="truncate max-w-32">{student.email}</span>
                        </div>
                        <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                          <Phone className="w-4 h-4" />
                          <span>{student.phone}</span>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      {student.leads && student.leads.length > 0 ? (
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                            student.leads[0].status
                          )}`}
                        >
                          {getStatusText(student.leads[0].status)}
                        </span>
                      ) : (
                        // Fallback for when leads are not joined
                        student.leads === undefined ? (
                          <span className="text-gray-400 text-xs">...</span>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )
                      )}
                    </td>
                    <td className="py-4 px-6">
                      <div className="text-sm text-gray-700 dark:text-gray-300 font-medium whitespace-nowrap">
                        {student.display_branch_name || '-'}
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {format(new Date(student.created_at), 'dd MMM yyyy', {
                          locale: tr
                        })}
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleStudentAction('documents', student)}
                          className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                          title="Evrakları Görüntüle"
                        >
                          <Folder className="w-4 h-4" />
                        </button>
                        {(user as any)?.unitType === 'finance' && (
                          <button
                            onClick={() => handleStudentAction('sendToOperations', student)}
                            className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                            title="Operasyona Gönder"
                          >
                            <Send className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleStudentAction('call', student)}
                          className="p-2 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
                          title="Ara"
                        >
                          <Phone className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleStudentAction('delete', student)}
                          className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          title="Sil"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {
        filteredStudents.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-200 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-gray-500 dark:text-gray-500" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Öğrenci bulunamadı
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Arama kriterlerinizi değiştirmeyi deneyin
            </p>
          </div>
        )
      }

      {
        selectedStudent && (
          <StudentDetailModal
            isOpen={showDocumentsModal}
            onClose={() => {
              setShowDocumentsModal(false);
              setSelectedStudent(null);
            }}
            studentId={selectedStudent?.id || ''}
            onUpdate={fetchStudents}
          />
        )
      }

      <AddStudentModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleAddStudent}
      />

      <SmsReminderModal
        isOpen={smsModalOpen}
        onClose={() => setSmsModalOpen(false)}
      />
    </div >
  );
};

export default StudentList;
